import pressionImg from '../assets/controle_pression.webp';
import acoustiqueImg from '../assets/ecoute_acoustique.webp';
import fluoresceineImg from '../assets/fluoresceine_uv.webp';
import gazImg from '../assets/gaz_traceur_detecteur.webp';
import thermoImg from '../assets/thermographie_chauffage_sol.webp';
import humidimetreImg from '../assets/humidimetre_mur.webp';
import inspectionImg from '../assets/inspection_camera_egout.webp';

const PARENT = '/techniques';

const technique = (t) => ({ parent: PARENT, eyebrow: 'Techniques de recherche de fuite', ...t });

export const techniques = [
  {
    path: '/techniques',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Nos techniques',
    summary: 'Neuf méthodes non destructives, choisies et combinées selon le type de fuite.',
    title: 'Techniques de recherche de fuite non destructive | Expomis',
    description: 'Thermographie, acoustique, gaz traceur, fluorescéine, fumigène, caméra, endoscopie, contrôle de pression, humidimètre : quelle technique pour quelle fuite.',
    eyebrow: 'Techniques de recherche de fuite',
    h1: 'Nos techniques de recherche de fuite',
    intro: [
      "Il n'existe pas de technique universelle de recherche de fuite. Une caméra thermique ne voit pas une fuite d'eau froide, l'écoute acoustique n'entend rien sur une évacuation, le gaz traceur demande de pouvoir isoler le tuyau. Expomis dispose de neuf méthodes non destructives, et les combine pour recouper les résultats avant de marquer l'emplacement d'une fuite.",
    ],
    sections: [
      {
        h2: 'Quelle technique pour quelle fuite ?',
        list: [
          'Toute intervention : [mesure d’humidité](/techniques/humidimetre) pour délimiter la zone, [contrôle de pression](/techniques/mise-en-pression) pour confirmer la fuite et identifier le circuit.',
          'Eau chaude, chauffage au sol : [caméra thermique](/techniques/camera-thermique), puis [gaz traceur](/techniques/gaz-traceur) si le contraste est insuffisant.',
          "Eau froide sous pression, conduite enterrée : [détection acoustique](/techniques/detection-acoustique) et gaz traceur.",
          'Évacuations et égouts : [fluorescéine](/techniques/fluoresceine), [test fumigène](/techniques/test-fumigene), [caméra d’inspection](/techniques/camera-canalisation).',
          'Infiltrations : mesure d’humidité, fluorescéine, caméra thermique.',
          'Zones cachées (faux plafond, gaine, doublage) : [endoscopie](/techniques/endoscopie).',
        ],
      },
    ],
    childrenTitle: 'Les neuf techniques en détail',
    children: [
      '/techniques/camera-thermique',
      '/techniques/detection-acoustique',
      '/techniques/gaz-traceur',
      '/techniques/fluoresceine',
      '/techniques/mise-en-pression',
      '/techniques/humidimetre',
      '/techniques/test-fumigene',
      '/techniques/camera-canalisation',
      '/techniques/endoscopie',
    ],
  },
  technique({
    path: '/techniques/camera-thermique',
    nav: 'Caméra thermique',
    summary: "Visualise la chaleur d'une fuite d'eau chaude ou de chauffage au sol, sans contact.",
    serviceType: 'Recherche de fuite par thermographie',
    title: 'Recherche de fuite par caméra thermique (thermographie) | Expomis',
    description: "Comment la caméra thermique localise une fuite d'eau chaude ou de chauffage au sol, dans quels cas elle fonctionne et quelles sont ses limites.",
    h1: 'Recherche de fuite par caméra thermique',
    image: thermoImg,
    imageAlt: 'Caméra thermique montrant les tubes d’un chauffage au sol et la tache de chaleur d’une fuite',
    intro: [
      "La thermographie infrarouge mesure à distance la température des surfaces et en fait une image. Quand de l'eau chaude s'échappe d'une canalisation ou d'un chauffage au sol, elle réchauffe la chape ou le mur autour du point de fuite : la caméra thermique fait apparaître cette tache sans rien toucher.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "La caméra ne voit pas à travers les murs : elle lit la température de la surface, et ce qui se passe derrière se déduit des écarts de température. Pour une fuite d'eau chaude ou de chauffage, on met le circuit en chauffe et on observe la chaleur se propager : le tracé des tubes apparaît d'abord, puis la zone où l'eau se répand.",
          "Sur une paroi mouillée, c'est l'inverse : l'évaporation refroidit la surface, et la zone humide ressort plus froide que le reste.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          '[Chauffage au sol](/recherche-fuite-chauffage) et canalisations de chauffage ;',
          "canalisations d'eau chaude sanitaire encastrées ;",
          "étendue d'une zone humide après une [infiltration](/recherche-infiltration) ou un dégât des eaux.",
        ],
      },
      {
        h2: 'Limites',
        body: [
          "Il faut un contraste de température. Une fuite d'eau froide dans un mur à température ambiante est difficile à voir. Un revêtement épais ou un parquet posé sur lambourdes atténue le signal, et les surfaces brillantes reflètent le rayonnement. Dans ces cas, nous passons au [gaz traceur](/techniques/gaz-traceur) ou à la [détection acoustique](/techniques/detection-acoustique).",
        ],
      },
    ],
    faq: [
      {
        q: 'La caméra thermique voit-elle à travers les murs ?',
        a: "Non. Elle mesure la température de la surface. C'est l'eau chaude qui, en s'échappant, modifie cette température et trahit l'emplacement de la fuite.",
      },
    ],
    related: ['/recherche-fuite-chauffage', '/techniques/gaz-traceur', '/techniques/humidimetre'],
  }),
  technique({
    path: '/techniques/detection-acoustique',
    nav: 'Détection acoustique',
    summary: "Écoute électroacoustique et corrélation : le bruit de l'eau sous pression qui s'échappe.",
    serviceType: 'Recherche de fuite par détection acoustique',
    title: 'Recherche de fuite par détection acoustique | Expomis',
    description: 'Écoute électroacoustique et corrélation : comment on localise une fuite sur une canalisation sous pression, encastrée ou enterrée, et les limites de la méthode.',
    h1: 'Recherche de fuite par détection acoustique',
    image: acoustiqueImg,
    imageAlt: "Illustration : écoute électroacoustique d'une canalisation enterrée avec un micro de sol",
    intro: [
      "Une canalisation sous pression qui fuit fait du bruit : l'eau qui s'échappe par une fissure émet un sifflement ou un bruissement, transmis par le tuyau et par le sol. L'écoute électroacoustique amplifie ces sons pour trouver l'endroit où ils sont les plus forts, c'est-à-dire à l'aplomb de la fuite.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "Un micro de sol très sensible est posé point par point le long du tracé de la canalisation. L'appareil amplifie et filtre le signal pour isoler le bruit de fuite des bruits parasites. Le niveau sonore augmente à mesure qu'on s'approche de la fuite.",
          "Sur les conduites plus longues, la corrélation acoustique place deux capteurs de part et d'autre de la zone suspecte. L'écart entre les instants où le bruit arrive à chaque capteur permet de calculer la position de la fuite.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          "canalisations d'eau potable sous pression, eau froide comprise ;",
          '[conduites enterrées](/recherche-fuite-canalisation) sous le jardin ou une allée ;',
          'canalisations sous dalle.',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "La méthode demande de la pression : elle ne fonctionne pas sur les [évacuations](/recherche-fuite-evacuation). Les fuites très faibles font peu de bruit, et le bruit ambiant (circulation, pompes, appareils en marche) gêne l'écoute. Les sols souples ou la terre meuble amortissent le son. Dans ces situations, le [gaz traceur](/techniques/gaz-traceur) prend le relais.",
        ],
      },
    ],
    related: ['/recherche-fuite-canalisation', '/techniques/gaz-traceur', '/surconsommation-eau'],
  }),
  technique({
    path: '/techniques/gaz-traceur',
    nav: 'Gaz traceur',
    summary: "Mélange azote-hydrogène qui remonte à la surface à l'aplomb des fuites les plus faibles.",
    serviceType: 'Recherche de fuite au gaz traceur',
    title: 'Recherche de fuite au gaz traceur (azote-hydrogène) | Expomis',
    description: "Le gaz traceur azote-hydrogène localise les petites fuites sous dalle ou enterrées. Fonctionnement, sécurité, cas d'usage et limites.",
    h1: 'Recherche de fuite au gaz traceur',
    image: gazImg,
    imageAlt: "Détecteur d'hydrogène et sonde de sol posée sur un carrelage pour localiser une fuite au gaz traceur",
    intro: [
      "Le gaz traceur est un mélange d'azote et d'hydrogène, non toxique et ininflammable dans cette proportion. Injecté dans une canalisation, il s'échappe par la fuite, traverse la chape, le carrelage ou la terre, et un détecteur très sensible le repère en surface, à l'aplomb du point de fuite.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "On coupe l'eau et on vide le tronçon concerné, puis on y injecte le gaz sous pression. L'hydrogène est la plus petite et la plus légère des molécules : il traverse les matériaux et remonte vers la surface. On balaie ensuite le tracé de la canalisation avec une sonde ; la concentration la plus forte indique la fuite.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          'fuites faibles et silencieuses, que l’écoute acoustique ne capte pas ;',
          '[canalisations sous dalle ou enterrées](/recherche-fuite-canalisation) ;',
          '[chauffage au sol](/recherche-fuite-chauffage) quand la caméra thermique ne donne pas un contraste suffisant.',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "Il faut pouvoir isoler et vider le tronçon testé. Certains revêtements très étanches ralentissent la remontée du gaz, et les courants d'air le dispersent. La méthode ne s'applique pas aux évacuations, qui ne sont pas étanches par nature : on utilise alors le [test fumigène](/techniques/test-fumigene).",
        ],
      },
    ],
    faq: [
      {
        q: 'Le gaz traceur est-il dangereux ?',
        a: "Non. Le mélange utilisé contient 95 % d'azote et 5 % d'hydrogène : il est ininflammable, non toxique, sans odeur, et se dissipe dans l'air.",
      },
    ],
    related: ['/recherche-fuite-canalisation', '/techniques/detection-acoustique', '/techniques/mise-en-pression'],
  }),
  technique({
    path: '/techniques/fluoresceine',
    nav: 'Fluorescéine',
    summary: "Colorant fluorescent qui montre d'où vient l'eau : évacuation, douche, terrasse.",
    serviceType: 'Recherche de fuite à la fluorescéine',
    title: 'Recherche de fuite à la fluorescéine | Expomis',
    description: "Le test à la fluorescéine identifie l'origine d'une fuite d'évacuation, de douche ou d'une infiltration de terrasse. Principe, usages et limites.",
    h1: 'Recherche de fuite à la fluorescéine',
    image: fluoresceineImg,
    imageAlt: "Fluorescéine révélée à la lampe UV : l'eau colorée s'échappe d'un raccord de siphon et coule sur le carrelage",
    intro: [
      "La fluorescéine est un colorant non toxique, vert fluorescent, visible à très faible concentration, surtout sous une lampe UV. On colore l'eau d'un appareil ou d'une zone précise, puis on observe où elle réapparaît : si l'eau colorée ressort dans la tache d'humidité, on sait d'où vient la fuite.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "Le test se fait source par source : la bonde de la douche, le lavabo, le WC, l'évacuation d'une terrasse, un relevé d'étanchéité. On fait couler l'eau colorée à un seul endroit à la fois, et on surveille la zone endommagée. Quand la couleur apparaît, la source est identifiée.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          '[évacuations](/recherche-fuite-evacuation) : quel appareil, quel raccord est en cause ;',
          '[douches et baignoires](/recherche-fuite-douche) : évacuation ou étanchéité ;',
          "[infiltrations](/recherche-infiltration) de terrasse, de balcon ou de toiture plate, par mise en eau ciblée ;",
          'vérification d’un raccordement : un appareil est-il bien relié à l’égout ?',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "La fluorescéine identifie la source, pas le point exact de la fuite sur le trajet. Elle suppose aussi que l'eau réapparaisse visiblement : si elle se perd dans le sol, on ne voit rien, et on passe à la [caméra d'inspection](/techniques/camera-canalisation) ou au [test fumigène](/techniques/test-fumigene).",
        ],
      },
    ],
    faq: [
      {
        q: 'La fluorescéine est-elle nocive ?',
        a: "Non. Elle est utilisée en très faible concentration et sert aussi au traçage des eaux souterraines, précisément parce qu'elle est sans danger pour l'environnement à ces doses.",
      },
    ],
    related: ['/recherche-fuite-douche', '/recherche-fuite-evacuation', '/recherche-infiltration'],
  }),
  technique({
    path: '/techniques/mise-en-pression',
    nav: 'Contrôle de pression',
    summary: 'Confirme la fuite et identifie le circuit en cause, avant toute localisation.',
    serviceType: 'Contrôle de pression des canalisations',
    title: 'Contrôle de pression : confirmer et isoler une fuite | Expomis',
    description: 'Le contrôle de pression confirme une fuite et identifie le circuit en cause : eau froide, eau chaude, chauffage, chauffage au sol. Principe et limites.',
    h1: 'Contrôle de pression : confirmer et isoler une fuite',
    image: pressionImg,
    imageAlt: 'Illustration : contrôle de pression d’une canalisation au manomètre',
    intro: [
      "Le contrôle de pression est souvent la première étape d'une recherche de fuite. On isole un circuit, on le met sous pression et on observe le manomètre : si la pression baisse, le circuit fuit. C'est simple, mais indispensable : on confirme qu'il y a bien une fuite, et on sait sur quel circuit la chercher.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "On ferme les vannes pour isoler chaque circuit : eau froide, eau chaude, boucle de recirculation, chauffage, et chaque boucle d'un chauffage au sol depuis le collecteur. Chacun est mis à une pression d'essai et observé pendant un temps donné. Le circuit dont la pression baisse est celui qui fuit.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          '[surconsommation d’eau](/surconsommation-eau) sans trace visible ;',
          '[chaudière qui perd de la pression](/recherche-fuite-chauffage) ;',
          'avant une localisation à la caméra thermique ou au gaz traceur, pour cibler le bon circuit ;',
          'après réparation, pour vérifier que le circuit est de nouveau étanche.',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "Le contrôle de pression dit si un circuit fuit, pas où exactement. Il ne s'applique pas aux évacuations, qui ne sont pas sous pression. Une vanne qui ne ferme plus bien peut fausser le résultat : nous en tenons compte dans l'interprétation.",
        ],
      },
    ],
    related: ['/techniques/gaz-traceur', '/techniques/detection-acoustique', '/surconsommation-eau'],
  }),
  technique({
    path: '/techniques/humidimetre',
    nav: 'Mesure d’humidité',
    summary: "Cartographie de la zone humide : son étendue, et le côté d'où vient l'eau.",
    serviceType: "Mesure d'humidité des matériaux",
    title: "Mesure d'humidité : cartographier une zone humide | Expomis",
    description: "L'humidimètre mesure l'humidité des matériaux pour délimiter une zone touchée, distinguer fuite, infiltration et remontées capillaires, et suivre le séchage.",
    h1: "Mesure d'humidité : cartographier une zone humide",
    image: humidimetreImg,
    imageAlt: "Humidimètre appliqué sur un mur taché d'humidité, peinture qui s'écaille",
    intro: [
      "L'humidimètre mesure la teneur en humidité d'un matériau : plâtre, béton, chape, bois, maçonnerie. En relevant des mesures en de nombreux points, on dresse une cartographie de la zone humide : son étendue, et surtout son gradient, qui indique de quel côté vient l'eau.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "Selon le matériau, on mesure en surface sans l'abîmer, ou par des pointes pour le bois et les mesures en profondeur. Les relevés sont pris selon une grille régulière. Les valeurs les plus élevées se trouvent au plus près de la source d'eau.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          'au début de chaque intervention, pour délimiter la zone touchée ;',
          'pour distinguer une fuite d’une [infiltration](/recherche-infiltration), de la condensation ou de remontées capillaires ;',
          'pour documenter les dommages dans un [dossier de dégât des eaux](/degat-des-eaux) ;',
          'pour suivre le séchage après réparation, avant de refaire les finitions.',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "L'humidimètre indique où se trouve l'eau, pas avec certitude d'où elle vient : il oriente la recherche, que d'autres techniques confirment. Les valeurs dépendent du matériau, elles se comparent entre elles sur un même support plutôt qu'en valeur absolue.",
        ],
      },
    ],
    related: ['/recherche-infiltration', '/recherche-fuite-virton', '/degat-des-eaux'],
  }),
  technique({
    path: '/techniques/test-fumigene',
    nav: 'Test fumigène',
    summary: "Fumée non toxique qui ressort par les défauts d'un réseau d'évacuation et révèle les odeurs.",
    serviceType: 'Test fumigène des évacuations',
    title: "Test fumigène : fuites d'évacuation et odeurs d'égout | Expomis",
    description: "Le test fumigène révèle les raccords non étanches, fissures et défauts d'un réseau d'évacuation ou d'égout, et l'origine des odeurs. Principe et limites.",
    h1: 'Test fumigène : trouver les défauts des évacuations',
    intro: [
      "Le test fumigène consiste à envoyer une fumée non toxique dans un réseau d'évacuation ou d'égout. La fumée suit le réseau et ressort par tous ses défauts : raccord mal emboîté, tuyau fissuré, siphon manquant, raccordement non étanche. Là où la fumée apparaît, l'eau s'échappe, ou les odeurs entrent.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "Un générateur envoie la fumée dans le réseau par un regard ou une ouverture de visite, en légère surpression. On parcourt ensuite le bâtiment : pièces, gaines, caves, pieds de descentes, abords extérieurs. Chaque sortie de fumée signale un défaut d'étanchéité. Un siphon sec ou absent laisse lui aussi passer la fumée, ce qui explique souvent des odeurs d'égout sans fuite d'eau.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          "odeurs d'égout dans un logement ou une cave ;",
          '[fuites sur les évacuations](/recherche-fuite-evacuation) et les descentes ;',
          'vérification des raccordements et de la ventilation du réseau.',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "Le réseau doit être accessible par un regard, un tampon ou une ouverture de visite. Si la fumée sort dans une zone invisible, sous une dalle par exemple, on complète par la [caméra d'inspection](/techniques/camera-canalisation).",
        ],
      },
    ],
    faq: [
      {
        q: 'La fumée est-elle dangereuse ?',
        a: "Non. Elle est non toxique et se dissipe rapidement ; il suffit d'aérer après le test.",
      },
    ],
    related: ['/recherche-fuite-evacuation', '/techniques/camera-canalisation', '/techniques/fluoresceine'],
  }),
  technique({
    path: '/techniques/camera-canalisation',
    nav: 'Caméra d’inspection',
    summary: "Caméra poussée dans les évacuations : fissures, joints déboîtés, racines, contre-pentes.",
    serviceType: 'Inspection caméra des canalisations',
    title: 'Inspection caméra des canalisations et égouts | Expomis',
    description: "L'inspection caméra filme l'intérieur des évacuations et égouts : fissures, joints déboîtés, racines, écrasement, contre-pentes. Principe et limites.",
    h1: 'Inspection caméra des canalisations et égouts',
    image: inspectionImg,
    imageAlt: "Écran d'une caméra d'inspection montrant une fissure dans une canalisation, câble descendu dans un regard",
    intro: [
      "Une caméra étanche, poussée au bout d'un câble dans le réseau d'évacuation, filme l'intérieur des canalisations. On voit directement l'état du tuyau : fissures, joints déboîtés, racines, écrasement, dépôts, contre-pentes. La longueur de câble déroulée situe le défaut depuis le point d'accès.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "La caméra est introduite par un regard, un tampon ou un té de visite, puis poussée progressivement dans la canalisation. L'image s'affiche en direct sur un écran, et le compteur de câble indique à quelle distance du point d'entrée se trouve chaque défaut. On sait ainsi quel tronçon est en cause et où ouvrir, au lieu de dégager toute la canalisation.",
          "Contrairement à la fluorescéine ou au fumigène, qui montrent qu'un défaut existe, la caméra montre sa nature : fissure, joint déboîté ou racine n'appellent pas la même réparation.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          "[évacuations enterrées ou sous dalle](/recherche-fuite-evacuation) ;",
          "raccordement de la maison à l'égout ;",
          'après un test fumigène ou à la fluorescéine, pour voir le défaut et décider de la réparation ;',
          'écoulements lents ou refoulements à répétition.',
        ],
      },
      {
        h2: 'Limites',
        body: [
          "Il faut un accès : regard, tampon, té de visite. Les petits diamètres et les coudes serrés limitent la progression, et une canalisation obstruée doit d'abord être débouchée.",
        ],
      },
    ],
    related: ['/recherche-fuite-evacuation', '/techniques/test-fumigene', '/techniques/endoscopie'],
  }),
  technique({
    path: '/techniques/endoscopie',
    nav: 'Endoscopie',
    summary: 'Voir dans un faux plafond, une gaine ou un doublage sans démonter.',
    serviceType: 'Endoscopie',
    title: 'Endoscopie : inspecter les zones inaccessibles | Expomis',
    description: "L'endoscope permet de voir dans un faux plafond, une gaine technique, un doublage ou sous une baignoire sans démonter. Usages et limites en recherche de fuite.",
    h1: 'Endoscopie : inspecter les zones inaccessibles',
    intro: [
      "Un endoscope est une petite caméra au bout d'une sonde flexible. On l'introduit par une ouverture existante (trappe, bouche de ventilation, joint, trou de fixation) ou par un petit perçage, pour voir dans un faux plafond, une gaine technique, un doublage ou sous une baignoire, sans démonter.",
    ],
    sections: [
      {
        h2: 'Comment ça fonctionne',
        body: [
          "La sonde est guidée dans la cavité pendant que l'image s'affiche sur un écran. On peut ainsi suivre une canalisation, repérer un raccord qui goutte, des traces d'écoulement ou un matériau mouillé, et photographier ce que l'on voit pour le rapport. Quand un petit perçage est nécessaire, il se rebouche facilement, sans commune mesure avec le démontage d'un habillage ou d'un plafond.",
        ],
      },
      {
        h2: 'Dans quels cas',
        list: [
          'vérifier un raccord caché derrière un habillage de baignoire ou un bâti-support de WC ;',
          'inspecter une gaine technique dans un immeuble ;',
          "suivre le cheminement de l'eau dans un faux plafond ou une cavité ;",
          "confirmer visuellement une fuite localisée par une autre méthode.",
        ],
      },
      {
        h2: 'Limites',
        body: [
          "L'endoscope ne voit que ce qui est dans une cavité : il est inutile pour une canalisation noyée dans une chape pleine. Sa portée est limitée à quelques mètres depuis le point d'entrée.",
        ],
      },
    ],
    related: ['/recherche-infiltration', '/recherche-fuite-douche', '/techniques/camera-canalisation'],
  }),
];
